import React from 'react';
import { AlertTriangle, Package2, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';

/**
 * Lastbærer-gaten ved avslutning av plukk (Teamwork 36334239).
 *
 * Erstatter det gamle window.confirm («OK» / «Avbryt»), der operatøren bare kunne
 * bekrefte at plukket skulle avsluttes uten lastbærer. Nå er andre valget en vei
 * VIDERE: «Registrer lastbærer(e)» åpner fordelingsbildet i stedet for å avbryte.
 *
 * Vises kun når backend har avvist fullføringen med CARRIER_REQUIRED
 * (settingen picking.requireCarrierOnComplete er på og ingen kollilinje har bærer).
 */
interface CarrierGateDialogProps {
  isOpen: boolean;
  /** Avslutt uten lastbærer — bekreftelsen stemples sporbart på plukklisten */
  onCompleteWithout: () => void;
  /** Åpne fordelingsbildet */
  onRegisterCarriers: () => void;
  onClose: () => void;
  busy?: boolean;
}

export const CarrierGateDialog: React.FC<CarrierGateDialogProps> = ({
  isOpen,
  onCompleteWithout,
  onRegisterCarriers,
  onClose,
  busy = false
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-5">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle size={22} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">
              {t('warehouse.modules.picking.delivery.noCarrierTitle', {}, 'Ingen lastbærer registrert')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('warehouse.modules.picking.delivery.noCarrierPrompt', {},
                'Plukket har ingen lastbærer. Vil du registrere lastbærere nå, eller avslutte uten? Avslutning uten lastbærer lagres på plukklisten.')}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 min-h-[44px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 order-3 sm:order-1"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={onCompleteWithout}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] border border-gray-400 rounded-lg text-gray-800 hover:bg-gray-100 disabled:opacity-50 order-2"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {t('warehouse.modules.picking.delivery.completeWithoutCarrier', {}, 'Avslutt uten lastbærer')}
          </button>
          <button
            onClick={onRegisterCarriers}
            disabled={busy}
            className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 order-1 sm:order-3"
          >
            <Package2 size={16} />
            {t('warehouse.modules.picking.delivery.registerCarriers', {}, 'Registrer lastbærer(e)')}
          </button>
        </div>
      </div>
    </div>
  );
};
